import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CategoriesService } from '../categories/categories.service';
import { ViewedProductsService } from '../viewed-products/viewed-products.service';
import { AuthenticationService } from '../authentication';
import { TestCustomerService } from '../test-customer/test-customer.service';
import {
  Cart,
  CartProduct,
  ResidentCart,
} from '../../../shared/models/cart.model';
import { DepartmentWithResidents } from '../../../shared/models/department.model';
import { Product } from '../../../shared/models/product.model';
import { Resident } from '../../../shared/models/resident.model';

/**
 * State half of the cart. Owns the cart that is currently being edited, the
 * list of all carts of the customer and the UI flags around them, plus the
 * in-memory manipulation of those carts. It never talks to the backend —
 * that is `CartApiService`.
 */
@Injectable({ providedIn: 'root' })
export class CartStateService {
  public departmentId: number;

  private cart$: BehaviorSubject<Cart> = new BehaviorSubject(new Cart());
  private allCarts$: BehaviorSubject<Cart[]> = new BehaviorSubject([]);
  private update$: BehaviorSubject<void> = new BehaviorSubject(null);
  private preventShoping$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );

  constructor(
    private categoriesService: CategoriesService,
    private viewedProductsService: ViewedProductsService,
    private authService: AuthenticationService,
    private testCustomerService: TestCustomerService
  ) {}

  // --- current cart -------------------------------------------------------

  get cart(): Cart {
    return this.cart$.value;
  }

  setCart(cart: Cart): void {
    this.cart$.next(cart);
  }

  getCart$(): Observable<Cart> {
    return this.cart$.asObservable();
  }

  getCartTotal$(): Observable<number> {
    return this.cart$.pipe(map(cart => cart.total));
  }

  // --- all carts ----------------------------------------------------------

  get allCarts(): Cart[] {
    return this.allCarts$.value;
  }

  setAllCarts(carts: Cart[] | null): void {
    this.allCarts$.next(carts);
  }

  getAllCarts(): Observable<Cart[]> {
    return this.allCarts$.asObservable();
  }

  replaceCart(newCart: Cart): void {
    const currentCarts = this.allCarts$.value;
    const index = currentCarts.findIndex(
      cart => cart.cartId === newCart.cartId
    );
    if (index !== -1) {
      currentCarts[index] = newCart;
      this.setAllCarts(currentCarts);
    }
  }

  // --- flags and change notifications ------------------------------------

  setUpdate(): void {
    this.update$.next();
  }

  getUpdate$(): Observable<void> {
    return this.update$.asObservable();
  }

  setPreventShoping(value: boolean): void {
    this.preventShoping$.next(value);
  }

  getPreventShoping(): Observable<boolean> {
    return this.preventShoping$.asObservable();
  }

  getActiveDepartment(id: number): void {
    this.departmentId = id;
    if (this.cart) {
      this.cart.departmentId = id;
    }
  }

  // --- cart contents ------------------------------------------------------

  getCurrentCart(residentId: number): ResidentCart | undefined {
    return this.cart.getResidentCart(residentId);
  }

  getProductQuantity(residentId: number, productId: number): number {
    const product = this.getResidentCartProduct(residentId, productId);
    return product ? product.quantity : 0;
  }

  getResidentCartProduct(
    residentId: number,
    productId: number
  ): CartProduct | undefined {
    const residentCart = this.cart.getResidentCart(residentId);
    return residentCart ? residentCart.getProduct(productId) : undefined;
  }

  setProductQuantity(
    residentId: number,
    productId: number,
    quantity: number
  ): void {
    const productAlreadyInCart = this.getResidentCartProduct(
      residentId,
      productId
    );
    if (productAlreadyInCart) {
      this.cart.changeResidentCartProductQuantity(
        residentId,
        productId,
        quantity
      );
    } else {
      this.cart.addProductToResidentCart(
        residentId,
        this.getNewProduct(productId, quantity)
      );
    }

    const changedProductCategory =
      this.viewedProductsService.findProductCategory(productId);
    if (quantity && quantity > 0) {
      this.categoriesService.addOrderedCategory(changedProductCategory);
    } else if (
      !this.checkIfExistsProductOfCurrentCategory(changedProductCategory)
    ) {
      this.categoriesService.deleteCategory(changedProductCategory);
    }
  }

  getNewProduct(productId: number, quantity: number): CartProduct {
    const product: Product =
      this.viewedProductsService.findAndSetCurrentProduct(productId, quantity);
    return product.convertToCartProduct();
  }

  checkIfExistsProductOfCurrentCategory(category: string): boolean {
    return this.cart.residentCarts.some(residentCart =>
      (residentCart.products ?? []).some(
        product => product.category === category
      )
    );
  }

  emptyCarts(departmentId: number): void {
    if (this.authService.loggedInAsTestCustomer()) {
      this.testCustomerService.emptyCart(departmentId);
    }
    const carts = this.allCarts$.value;
    carts.forEach(cart => {
      if (cart.departmentId === departmentId) {
        cart.residentCarts.forEach(residentCart => residentCart.emptyCart());
        cart.recalculateCartTotal();
      }
    });
    this.setAllCarts(carts);
  }

  createEmptyCartForDepartmentWithResidents(
    residents: Resident[],
    departmentId: number
  ): Cart {
    return new Cart({
      departmentId: departmentId,
      departmentName: '',
      residentCarts: residents.map(
        (resident: Resident) => new ResidentCart({ residentId: resident.id })
      ),
    });
  }

  // --- ordered categories -------------------------------------------------

  writeCartsValue(): void {
    this.categoriesService.deleteAllOrderedCategories();
    this.setOrderedCategoriesFromReceivedCart();
  }

  setOrderedCategoriesFromReceivedCart(): void {
    for (const residentCart of this.cart.residentCarts) {
      for (const product of residentCart.products ?? []) {
        this.categoriesService.addOrderedCategory(product.category);
      }
    }
  }

  // --- department synchronisation -----------------------------------------

  deleteInexistentResidentsCarts(department: DepartmentWithResidents): void {
    if (!department || department.departmentId !== this.cart.departmentId)
      return;
    const departmentResidentsIds = department.people.map(
      (resident: Resident) => resident.id
    );
    this.cart.filterDeletedResidents(departmentResidentsIds);
  }
}
